import { useRevealOnMount } from './reveal';
import './KeptPauseConfirm.css';

/**
 * "Another "Vacuum · L4-Ballroom" keeps the old time until Tue 03/Nov.
 * Pause it too?" -- the one question Pause asks, and only when an edit
 * split the schedule in two and the other row is still to run
 * (`planPartners`): the old time kept before it, or the new time that
 * takes over after it. Pausing this one alone leaves that one running,
 * which is not what "pause the Ballroom vacuum" means to most people --
 * and sometimes is, so the operator chooses. Resume asks the same of a
 * paused other row (`verb`), so "Pause both" can be undone in one go.
 *
 * Every place a schedule is paused draws it (the Agenda's schedule
 * panel, the Day and Week panel, the Month sidebar, the phone sheets),
 * each in its own look: `noteClass` / `btnClass`. Delete asks it too
 * (`verb: 'delete'`, the calendar's delete dialog), with the number on
 * the button -- "Delete all 3" -- since nothing brings a deleted row
 * back.
 *
 * With more than one other row, each is named on its own line (`list`,
 * `planPartners`), as the delete dialog names them: "Another: every day
 * at 09:00, from Sat 31/Oct until Tue 03/Nov".
 *
 * It opens where the button that asked was pressed, which can be below
 * what a short panel shows: it scrolls itself into view and takes focus
 * (`useRevealOnMount`) -- on Cancel, for every verb: Enter held on
 * "Pause schedule" or "Delete the schedule" repeats, and must not land on
 * "Pause all" or "Delete all 3". Cancel gives focus back to the button
 * that asked.
 */
export function KeptPauseConfirm(props: {
  /** `planPartners`' text; empty when the rows are named above it. */
  text: string;
  /** One line for each other row, after `text` (`planPartners`' list). */
  list?: readonly string[];
  /** How many other schedules "too" takes along. */
  count: number;
  /** What the button that asks does; Pause unless said. */
  verb?: 'pause' | 'resume' | 'delete';
  /** The "all" button's look when it differs (a delete's danger). */
  bothClass?: string;
  disabled?: boolean;
  onBoth: () => void;
  onOne: () => void;
  onCancel: () => void;
  className?: string;
  noteClass: string;
  actionsClass: string;
  btnClass: string;
  testId: string;
}) {
  const verb = props.verb === 'resume' ? 'Resume' : props.verb === 'delete' ? 'Delete' : 'Pause';
  const all =
    props.verb === 'delete'
      ? `Delete all ${props.count + 1}`
      : props.count === 1
        ? `${verb} both`
        : `${verb} all`;
  const ref = useRevealOnMount<HTMLDivElement>((el) =>
    el.querySelector<HTMLElement>(`[data-testid="${props.testId}-cancel"]`),
  );
  const question = props.count === 1 ? `${verb} it too?` : `${verb} them too?`;
  const list = props.list ?? [];
  return (
    <div
      ref={ref}
      className={props.className}
      role="group"
      aria-label={`Confirm ${verb.toLowerCase()}`}
      data-testid={props.testId}
    >
      {list.length > 0 ? (
        // One note, as the sentence is: what leads in, the rows, the
        // question.
        <div className={`${props.noteClass} nx-kept-confirm__note`}>
          {props.text && <p>{props.text}</p>}
          <ul className="nx-kept-confirm__rows" data-testid={`${props.testId}-rows`}>
            {list.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
          <p>{question}</p>
        </div>
      ) : (
        <p className={props.noteClass}>
          {props.text ? `${props.text} ` : ''}
          {question}
        </p>
      )}
      <div className={props.actionsClass}>
        <button
          type="button"
          className={props.bothClass ?? props.btnClass}
          disabled={props.disabled}
          onClick={props.onBoth}
          data-testid={`${props.testId}-both`}
        >
          {all}
        </button>
        <button
          type="button"
          className={props.btnClass}
          disabled={props.disabled}
          onClick={props.onOne}
          data-testid={`${props.testId}-one`}
        >
          Only this one
        </button>
        <button
          type="button"
          className={props.btnClass}
          onClick={props.onCancel}
          data-testid={`${props.testId}-cancel`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
